import { ClientLoaderFunctionArgs, useRouteLoaderData } from 'react-router';
import { gql } from '@/__generated__';
import { preloadQuery } from '@/apollo/apollo-client';
import { getApolloClient } from '@/apollo/apollo-client-instance';
import { sessionContext } from '@/session';
import { Route } from './+types/route';
import { UnauthenticatedTournamentQuery } from './unauthenticated-tournament-query';

const TournamentQuery = gql(`
query TournamentQuery($id: ID!) {
  tournament(id: $id) {
    ...TournamentBannerFragment
    ...TournamentMySubmissionsFragment
    ...TournamentOverviewFragment
    ...TournamentAwardsFragment
    ...TournamentPostFormFragment
  }
}
`);

const TournamentSubmissionsQuery = gql(`
query TournamentSubmissionsQuery($id: ID!, $limit: Int!, $nextToken: String) {
  tournament(id: $id) {
    id
    submissions(limit: $limit, nextToken: $nextToken) {
      items {
          ...TournamentPostCardFragment
      }
      nextToken
    }
  }
}
`);

export const tournamentLoader = async (args: ClientLoaderFunctionArgs) => {
  const authenticated = args.context.get(sessionContext).kind !== 'guest';
  const loader = authenticated
    ? authenticatedTournamentLoader
    : unauthenticatedTournamentLoader;
  return {
    ...(await loader(args as Route.ClientLoaderArgs)),
    authenticated,
  };
};

export const authenticatedTournamentLoader = ({
  params: { id },
}: Route.ClientLoaderArgs) => {
  return {
    queryRef: preloadQuery(TournamentQuery, {
      variables: {
        id,
      },
      fetchPolicy: 'cache-and-network',
      returnPartialData: true,
    }),
    submissionsQueryRef: preloadQuery(TournamentSubmissionsQuery, {
      variables: {
        id,
        limit: 5,
        nextToken: null,
      },
      fetchPolicy: 'cache-and-network',
    }),
  };
};

const unauthenticatedTournamentLoader = async ({
  params,
  serverLoader,
}: Route.ClientLoaderArgs) => {
  const id = params.id as string;

  const unauthenticatedData = (await serverLoader())?.unauthenticatedData;

  if (unauthenticatedData) {
    getApolloClient().writeQuery({
      query: UnauthenticatedTournamentQuery,
      variables: { id },
      data: unauthenticatedData,
    });
  }

  return {
    unauthenticatedData,
    queryRef: preloadQuery(UnauthenticatedTournamentQuery, {
      variables: { id },
    }),
  };
};

export type AuthenticatedTournamentLoaderData = ReturnType<
  typeof authenticatedTournamentLoader
> & {
  authenticated: boolean;
};

export type UnauthenticatedTournamentLoaderData = Awaited<
  ReturnType<typeof unauthenticatedTournamentLoader>
> & { authenticated: boolean };

export const useTournamentLoaderData = () =>
  useRouteLoaderData(
    'routes/_app.p.tournaments_.$id',
  ) as AuthenticatedTournamentLoaderData & UnauthenticatedTournamentLoaderData;
